import React, {useEffect, useState} from 'react';
import {func, object} from 'prop-types';
import {compose} from 'redux';
import {connect} from 'react-redux';
import {path, pathOr} from 'ramda';

import config from '../../config';
import {ensureCurrentUser, ensureStripeCustomer, ensurePaymentMethodCard} from '../../util/data';
import {propTypes} from '../../util/types';
import {
  getCountryValue,
  getStripeCustomerForPaymentMethodSave,
  isUsableStripePaymentMethod,
  shouldForceCartesBancairesResave,
} from '../../util/stripe';
import {savePaymentMethod, deletePaymentMethod} from '../../ducks/paymentMethods.duck';
import {handleCardSetup} from '../../ducks/stripe.duck';
import {manageDisableScrolling} from '../../ducks/UI.duck';
import {
  SavedCardDetails,
} from '../../components';
import {PaymentMethodsForm} from '../../forms';
import {
  createStripeSetupIntent,
  stripeCustomer,
} from '../AccountPaymentsPage/components/PaymentMethodsTab/PaymentMethodsTab.duck.js';
import css from './CheckoutProposalPage.module.css';
import {injectIntl} from '../../util/reactIntl';
import {verifyDiscountCode} from '../CheckoutPage/CheckoutPage.duck';

const SaveStripePaymentMethod = props => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [cardState, setCardState] = useState(null);
  const [hasValidPaymentMethod, setHasValidPaymentMethod] = useState(false);
  const {
    currentUser,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    deletePaymentMethodInProgress,
    onCreateSetupIntent,
    onHandleCardSetup,
    onSavePaymentMethod,
    onDeletePaymentMethod,
    fetchStripeCustomer,
    onManageDisableScrolling,
    intl,
    isLoading,
    stripePaymentMethodDetails,
  } = props;

  useEffect(() => {
    fetchStripeCustomer();
  }, [fetchStripeCustomer]);

  const ensuredCurrentUser = ensureCurrentUser(currentUser);
  const currentUserLoaded = !!ensuredCurrentUser.id;
  const hasDefaultPaymentMethod =
    currentUser &&
    ensureStripeCustomer(currentUser.stripeCustomer).attributes.stripeCustomerId &&
    ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).id;
  const hasUsablePaymentMethod = isUsableStripePaymentMethod(stripePaymentMethodDetails);
  const hasSetupIntent = !!path(['attributes', 'profile', 'privateData', 'clientSecretSetupIntent'], currentUser);
  const requiresCardResave = shouldForceCartesBancairesResave(stripePaymentMethodDetails);
  useEffect(() => {
    setHasValidPaymentMethod(
      hasDefaultPaymentMethod && hasUsablePaymentMethod && hasSetupIntent && !requiresCardResave
    );
  }, [hasSetupIntent, hasDefaultPaymentMethod, hasUsablePaymentMethod, requiresCardResave]);
  const getClientSecret = setupIntent => {
    return setupIntent && setupIntent.attributes ? setupIntent.attributes.clientSecret : null;
  };
  const getPaymentParams = (currentUser, formValues) => {
    const {name, addressLine1, addressLine2, postal, state, city, country} = formValues;
    const addressMaybe =
      addressLine1 && postal
        ? {
          address: {
            city: city,
            country: country,
            line1: addressLine1,
            line2: addressLine2,
            postal_code: postal,
            state: state,
          },
        }
        : {};
    const billingDetails = {
      name,
      email: ensureCurrentUser(currentUser).attributes.email,
      ...addressMaybe,
    };

    const paymentParams = {
      payment_method_data: {
        billing_details: billingDetails,
      },
    };

    return paymentParams;
  };

  const handleSubmitCard = params => {
    setIsSubmitting(true);
    const ensuredCurrentUser = ensureCurrentUser(currentUser);
    const stripeCustomer = ensuredCurrentUser.stripeCustomer;
    const stripeCustomerForSave = getStripeCustomerForPaymentMethodSave(
      stripeCustomer,
      stripePaymentMethodDetails
    );
    const {stripe, card, formValues} = params;

    onCreateSetupIntent()
      .then(setupIntent => {
        const stripeParams = {
          stripe,
          card,
          setupIntentClientSecret: getClientSecret(setupIntent),
          paymentParams: getPaymentParams(currentUser, formValues),
        };

        return onHandleCardSetup(stripeParams);
      })
      .then(result => {
        const newPaymentMethod = result.setupIntent.payment_method;
        const currentUserEmail = path(['attributes', 'email'], currentUser);
        // Note: stripe.handleCardSetup might return an error inside successful call (200), but those are rejected in thunk functions.

        return onSavePaymentMethod(stripeCustomerForSave, newPaymentMethod, currentUserEmail);
      })
      .then(() => {
        // Update currentUser entity and its sub entities: stripeCustomer and defaultPaymentMethod
        fetchStripeCustomer();
        setIsSubmitting(false);
        setCardState('default');
      })
      .catch(error => {
        console.error(error);
        setIsSubmitting(false);
      });
  };
  const handleRemovePaymentMethod = (e) => {
    e.preventDefault();
    onDeletePaymentMethod().then(() => {
      fetchStripeCustomer();
    });
  };
  // Get first and last name of the current user and use it in the StripePaymentForm to autofill the name field
  const userName = currentUserLoaded
    ? `${ensuredCurrentUser.attributes.profile.firstName} ${ensuredCurrentUser.attributes.profile.lastName}`
    : null;
  const countryValue = getCountryValue(config.locale);

  const initalValuesForStripePayment = {name: userName, country: countryValue};

  const card = hasDefaultPaymentMethod
    ? ensurePaymentMethodCard(currentUser.stripeCustomer.defaultPaymentMethod).attributes.card
    : null;

  const showForm = cardState === 'replaceCard' || !hasDefaultPaymentMethod || !hasValidPaymentMethod;
  const showCardDetails = !!hasDefaultPaymentMethod && hasUsablePaymentMethod && !requiresCardResave;

  return (
    <div className={css.bodyContainer}>
      <div>
        {hasValidPaymentMethod && showCardDetails ? (
          <div className={css.SavedCardDetails}>
            <SavedCardDetails
              card={card}
              onManageDisableScrolling={onManageDisableScrolling}
              onChange={setCardState}
              onDeleteCard={handleRemovePaymentMethod}
              deletePaymentMethodInProgress={deletePaymentMethodInProgress || isLoading}
            />
          </div>
        ) : null}
        {showForm ? (
          <div className={css.PaymentMethodsForm}>
            <h1 className={css.headingSavePaymentMethod}>{intl.formatMessage(
              {id: `CheckoutProposalPage.headingSavePaymentMethod`}
            )}</h1>
            <PaymentMethodsForm
              className={css.paymentForm}
              formId="PaymentMethodsForm"
              initialValues={initalValuesForStripePayment}
              onSubmit={handleSubmitCard}
              handleRemovePaymentMethod={handleRemovePaymentMethod}
              hasDefaultPaymentMethod={hasDefaultPaymentMethod}
              addPaymentMethodError={addPaymentMethodError}
              deletePaymentMethodError={deletePaymentMethodError}
              createStripeCustomerError={createStripeCustomerError}
              handleCardSetupError={handleCardSetupError}
              inProgress={isSubmitting || isLoading}
            />
          </div>
        ) : null}
      </div>
    </div>
  );
};

SaveStripePaymentMethod.defaultProps = {
  currentUser: null,
  addPaymentMethodError: null,
  deletePaymentMethodError: null,
  createStripeCustomerError: null,
  handleCardSetupError: null,
};

SaveStripePaymentMethod.propTypes = {
  currentUser: propTypes.currentUser,
  addPaymentMethodError: object,
  deletePaymentMethodError: object,
  createStripeCustomerError: object,
  handleCardSetupError: object,
  onCreateSetupIntent: func.isRequired,
  onHandleCardSetup: func.isRequired,
  onSavePaymentMethod: func.isRequired,
  onDeletePaymentMethod: func.isRequired,
  fetchStripeCustomer: func.isRequired,
};

const mapStateToProps = state => {
  const {currentUser, isLoading} = state.user;
  const {
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
  } = state.paymentMethods;
  const {stripeCustomerFetched, stripePaymentMethodDetails} = state.PaymentMethodsTab;
  const {handleCardSetupError} = state.stripe;

  return {
    isLoading,
    currentUser,
    deletePaymentMethodInProgress,
    addPaymentMethodError,
    deletePaymentMethodError,
    createStripeCustomerError,
    handleCardSetupError,
    stripeCustomerFetched,
    stripePaymentMethodDetails,
  };
};

const mapDispatchToProps = dispatch => ({
  onManageDisableScrolling: (componentId, disableScrolling) =>
    dispatch(manageDisableScrolling(componentId, disableScrolling)),
  fetchStripeCustomer: () => dispatch(stripeCustomer()),
  onHandleCardSetup: params => dispatch(handleCardSetup(params)),
  onCreateSetupIntent: params => dispatch(createStripeSetupIntent(params)),
  onSavePaymentMethod: (stripeCustomer, newPaymentMethod, stripeCustomerEmail) =>
    dispatch(savePaymentMethod(stripeCustomer, newPaymentMethod, stripeCustomerEmail)),
  onDeletePaymentMethod: params => dispatch(deletePaymentMethod(params)),
  onVerifyDiscountCode: (params) => dispatch(verifyDiscountCode(params)),
});

export default compose(
  connect(
    mapStateToProps,
    mapDispatchToProps,
  ),
  injectIntl,
)(SaveStripePaymentMethod);
